import { useState, useEffect, useRef, useCallback } from 'react'
import { consumer } from '../lib/actionCable'

/**
 * Subscribes to BeatContentChannel for a player session.
 * Accumulates streamed chunks by beat_id and signals completion.
 *
 * @param {string|null} sessionId - Player session ID to subscribe to
 * @returns {{ streamingContent: Object, isStreaming: Function, clearBeat: Function }}
 *   - streamingContent: { [beatId]: { text: string, complete: boolean } }
 *   - isStreaming(beatId): whether that beat is actively receiving chunks
 *   - clearBeat(beatId): remove streaming state for a beat
 */
export function useBeatContentStream(sessionId) {
  const [streamingContent, setStreamingContent] = useState({})
  const subscriptionRef = useRef(null)
  // Store chunks keyed by order to reconstruct text in correct sequence
  const chunksRef = useRef({})

  useEffect(() => {
    if (!sessionId) return

    const subscription = consumer.subscriptions.create(
      { channel: 'BeatContentChannel', session_id: sessionId },
      {
        received(data) {
          const { body, complete, order, beat_id } = data
          if (!beat_id) return

          // Store chunk by order number
          if (!chunksRef.current[beat_id]) chunksRef.current[beat_id] = {}
          chunksRef.current[beat_id][order] = body || ''

          // Rebuild text from contiguous chunks starting at 0
          // If chunk 2 is missing, only show 0 and 1 — no skipping ahead
          const chunks = chunksRef.current[beat_id]
          let text = ''
          for (let i = 0; ; i++) {
            if (!(i in chunks)) break
            text += chunks[i]
          }

          setStreamingContent((prev) => ({
            ...prev,
            [beat_id]: { text, complete: !!complete },
          }))
        },
      }
    )

    subscriptionRef.current = subscription

    return () => {
      subscription.unsubscribe()
      subscriptionRef.current = null
      chunksRef.current = {}
    }
  }, [sessionId])

  const isStreaming = useCallback(
    (beatId) => {
      const entry = streamingContent[beatId]
      return entry ? !entry.complete && entry.text.length > 0 : false
    },
    [streamingContent]
  )

  const clearBeat = useCallback((beatId) => {
    delete chunksRef.current[beatId]
    setStreamingContent((prev) => {
      const next = { ...prev }
      delete next[beatId]
      return next
    })
  }, [])

  const clearBeats = useCallback((beatIds) => {
    beatIds.forEach((id) => delete chunksRef.current[id])
    setStreamingContent((prev) => {
      const next = { ...prev }
      beatIds.forEach((id) => delete next[id])
      return next
    })
  }, [])

  return { streamingContent, isStreaming, clearBeat, clearBeats }
}
